import {
  Observable,
  catchError,
  switchMap,
  timeout as _timeout,
  filter,
  take,
  interval,
  map,
  of,
  concat,
  pipe,
  OperatorFunction,
  tap,
} from "rxjs";

export type ProcessEvent<TDataStart, TDataComplete> =
  | { status: "not_started"; data: null }
  | { status: "start"; data: { data: TDataStart; requestId: string } }
  | { status: "complete"; data: TDataComplete }
  | { status: "error"; data: { error: unknown; subject: GenerationError } };

export type GenerationStatus = ProcessEvent<object, object>["status"];

export const enum GenerationError {
  GENERATION_FAILED = "generation_failed",
  ALREADY_RUN = "already_generating",
}

type StatusFetcher<T> = (
  requestId: string,
) => Observable<{ status: string; data: T }>;

export function tapWhen<T>(
  predicate: (value: T) => boolean,
  sideEffect: (value: T) => void,
) {
  return tap<T>((value: T) => {
    if (predicate(value)) {
      sideEffect(value);
    }
  });
}

export function polling<
  TDataStart extends { requestId: string },
  TDataComplete,
>(
  fetchStatus: StatusFetcher<TDataComplete>,
  timeout: number,
  delay: number,
): OperatorFunction<
  TDataStart,
  ProcessEvent<Omit<TDataStart, "requestId">, TDataComplete>
> {
  const stoppingStatuses = ["completed", "failed", "not_found"];
  const checkRequestFinishedBystatus = (status: string) =>
    stoppingStatuses.includes(status);
  const mapResponse = ({
    status,
    data,
  }: {
    status: string;
    data: TDataComplete;
  }) => {
    if (status === "completed") {
      return { status: "complete" as const, data };
    }
    return {
      status: "error" as const,
      data: {
        error: "generation failed",
        subject: GenerationError.GENERATION_FAILED,
      },
    };
  };

  return pipe(
    switchMap(({ requestId, ...data }: TDataStart) =>
      concat(
        of({ status: "start" as const, data: { data, requestId } }),
        interval(delay).pipe(
          switchMap(() => fetchStatus(requestId)),
          filter((res) => checkRequestFinishedBystatus(res.status)),
          take(1),
          _timeout(timeout),
          map(mapResponse),
        ),
      ),
    ),
    catchError((err) =>
      of({
        status: "error" as const,
        data: {
          error: err,
          subject: GenerationError.GENERATION_FAILED,
        },
      }),
    ),
  );
}
